/**
 * Servicio para la gestión de empleados
 * Fecha: 16 Oct 2025
 */

import { apiRequest, API_URL } from '@/shared/lib/apiUtils';
import { httpRequest } from '@/shared/lib/http';
import type {
  Employee,
  CreateEmployeeDto,
  UpdateEmployeeDto,
  EmployeeFilters,
  CompletenessResponse,
  CanEnableResponse,
} from '@/types/employee.types';

/** Lo que devuelve `/employees/select`: lo justo para un desplegable. */
export interface EmpleadoDeSelect {
  id: number;
  fullName: string;
  areaName: string | null;
  puestoName: string | null;
}

/**
 * Servicio de empleados con todos los endpoints disponibles
 */
export const employeesService = {
  /**
   * La plantilla para un desplegable: solo nombre, area y puesto.
   *
   * Es la que se usa cuando hay que elegir a alguien de la empresa y no
   * consultar su expediente. `getAll` pide el permiso de expedientes y trae
   * CURP, RFC, NSS y sueldo, asi que a quien solo va a elegir un nombre le
   * respondia 403 y el desplegable se quedaba vacio.
   */
  getForSelect: async (): Promise<EmpleadoDeSelect[]> => {
    return (await httpRequest<EmpleadoDeSelect[]>('/employees/select')) ?? [];
  },

  /**
   * Listar empleados con filtros opcionales
   */
  getAll: async (filters?: EmployeeFilters): Promise<Employee[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    const queryString = params.toString();
    const url = queryString ? `${API_URL}/employees?${queryString}` : `${API_URL}/employees`;
    return apiRequest<Employee[]>(url);
  },

  /**
   * Obtener empleado por ID con relaciones
   */
  getById: async (id: number): Promise<Employee> => {
    return apiRequest<Employee>(`${API_URL}/employees/${id}`);
  },

  /**
   * Crear nuevo empleado
   */
  create: async (data: CreateEmployeeDto): Promise<Employee> => {
    return apiRequest<Employee>(`${API_URL}/employees`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Actualizar empleado existente
   */
  update: async (id: number, data: UpdateEmployeeDto): Promise<Employee> => {
    return apiRequest<Employee>(`${API_URL}/employees/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Obtener porcentaje de completitud del expediente
   */
  getCompleteness: async (id: number): Promise<CompletenessResponse> => {
    return apiRequest<CompletenessResponse>(`${API_URL}/employees/${id}/completeness`);
  },

  /**
   * Verificar si puede habilitar IMSS
   */
  canEnableImss: async (id: number): Promise<CanEnableResponse> => {
    return apiRequest<CanEnableResponse>(`${API_URL}/employees/${id}/can-enable-imss`);
  },

  /**
   * Verificar si puede habilitar ERP
   */
  canEnableErp: async (id: number): Promise<CanEnableResponse> => {
    return apiRequest<CanEnableResponse>(`${API_URL}/employees/${id}/can-enable-erp`);
  },

  /**
   * Habilitar IMSS para el empleado
   */
  enableImss: async (id: number): Promise<Employee> => {
    return apiRequest<Employee>(`${API_URL}/employees/${id}/enable-imss`, {
      method: 'POST',
    });
  },

  /**
   * Habilitar ERP para el empleado
   */
  enableErp: async (id: number): Promise<Employee> => {
    return apiRequest<Employee>(`${API_URL}/employees/${id}/enable-erp`, {
      method: 'POST',
    });
  },

  /**
   * Deshabilitar IMSS para el empleado
   */
  disableImss: async (id: number): Promise<Employee> => {
    return apiRequest<Employee>(`${API_URL}/employees/${id}/disable-imss`, {
      method: 'POST',
    });
  },

  /**
   * Deshabilitar ERP para el empleado
   */
  disableErp: async (id: number): Promise<Employee> => {
    return apiRequest<Employee>(`${API_URL}/employees/${id}/disable-erp`, {
      method: 'POST',
    });
  },

  /**
   * Recalcular porcentaje de completitud
   */
  recalculateCompleteness: async (id: number): Promise<void> => {
    await apiRequest<void>(`${API_URL}/employees/${id}/recalculate-completeness`, {
      method: 'POST',
    });
  },
};
